import { createSlice } from "@reduxjs/toolkit";
import * as Actions from "../actions";
import {
  dataTorrePesadosReducer,
  roteirosAlertasReducer,
  roteirosCriticidadeReducer,
  safraDiaEvolucaoReducer,
  safraDiaPlanejadaReducer,
} from "../reducers";

import { InitialState } from "./models";

const initialState: InitialState = {
  requestGetParams: {
    filiaisIds: [],
    transportadorasIds: [],
    dataRoteiros: "",
  },
  roteirosAlertas: {
    data: [],
    isLoading: false,
    error: null,
  },
  roteirosCriticidade: {
    data: [],
    isLoading: false,
    error: null,
  },
  safraDia: {
    evolucao: {
      data: {
        totalRoteirosEmEvolucao: 0,
        totalEntregasEmEvolucao: 0,
        totalEntregasSemTracking: 0,
        totalEntregasComInsucesso: 0,
        totalEntregue: 0,
        percentualRealizado: 0,
        percentualNaoRealizado: 0,
      },
      isLoading: false,
      error: null,
    },
    planejado: {
      data: {
        totalRoteirosPlanejados: 0,
        totalPedidosEntregaPlanejados: 0,
      },
      isLoading: false,
      error: null,
    },
  },
  showFiltersForm: false,
  filterOptions: {
    availableFiliais: [],
    availableTransportadoras: [],
  },
  filtersCSV: {
    dataInicial: "",
    dataFinal: "",
    filiaisIds: "",
    transportadorasIds: "",
    roteirosAceitos: true,
    pedidosColetados: "",
    statusEvolucao: "",
    codigoRoteiro: "",
  },
  filtersForm: {
    filiaisIds: [],
    transportadorasIds: [],
    statusColetaRoteiros: null,
    tipoRoteiros: [],
    isManualSubmit: false,
  },
  headersCsv: [
    { label: "Roteiro", key: "roteiro.codigo", encrypted: false },
    { label: "Motorista", key: "nomeMotorista", encrypted: false },
    { label: "Código Interno", key: "codigoInterno", encrypted: false },
    { label: "Número da Nota Fiscal", key: "notaFiscal", encrypted: false },
    { label: "Número Pedido", key: "numeroPedido", encrypted: false },
    { label: "Criado Em", key: "dataCriacao", encrypted: false },
    {
      label: "Status",
      key: "statusEntrega",
      encrypted: false,
    },
    {
      label: "Código Filial",
      key: "filial.codigoFilial",
      encrypted: false,
    },
    {
      label: "Nome Filial",
      key: "filial.nomeFilial",
      encrypted: false,
    },
    {
      label: "Nome Cliente",
      key: "nomeCliente",
      encrypted: false,
    },
    {
      label: "Transportadora",
      key: "transportadora.nome",
      encrypted: false,
    },
    {
      label: "Status Evolução",
      key: "statusEvolucao",
      encrypted: false,
    },
    {
      label: "Coleta CD",
      key: "statusColeta",
      encrypted: false,
    },
    {
      label: "Tipo",
      key: "tipo",
      encrypted: false,
    },
  ],
  activeSubtab: "",
  configTabs: {
    tabInView: 0,
    tabs: [
      {
        title: "LML - Visão Exceção",
        index: 0,
        active: true,
        name: "visaoExcecao",
      },
    ],
  },
};

const torreLmlSlice = createSlice({
  name: "torreLmlApp/torreLml",
  initialState,
  reducers: {
    changeTab: (state, action) => {
      state.configTabs.tabInView = action.payload;
      if (action.payload === 0) state.configTabs.tabs[1].active = false;
    },
    setActiveSubtabReducer: (state, action) => {
      state.activeSubtab = action.payload;
    },
    setFiltersForm: (state, action) => {
      state.filtersForm = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      Actions.getFiliaisTransportadoras.fulfilled,
      dataTorrePesadosReducer
    );

    // Safra Dia Evolucao
    builder.addCase(Actions.getSafraDiaEvolucao.pending, (state) => {
      state.safraDia.evolucao.isLoading = true;
      state.safraDia.evolucao.error = null;
    });

    builder.addCase(
      Actions.getSafraDiaEvolucao.fulfilled,
      safraDiaEvolucaoReducer
    );

    builder.addCase(Actions.getSafraDiaEvolucao.rejected, (state, action) => {
      state.safraDia.evolucao.isLoading = false;
      state.safraDia.evolucao.error =
        action.error.message || "Erro ao carregar evolução";
    });

    // Safra Dia Planejado
    builder.addCase(Actions.getSafraPlanejada.pending, (state) => {
      state.safraDia.planejado.isLoading = true;
      state.safraDia.planejado.error = null;
    });

    builder.addCase(
      Actions.getSafraPlanejada.fulfilled,
      safraDiaPlanejadaReducer
    );

    builder.addCase(Actions.getSafraPlanejada.rejected, (state, action) => {
      state.safraDia.planejado.isLoading = false;
      state.safraDia.planejado.error =
        action.error.message || "Erro ao carregar planejado";
    });

    // Roteiros Alertas
    builder.addCase(Actions.getRoteirosAlertas.pending, (state) => {
      state.roteirosAlertas.isLoading = true;
      state.roteirosAlertas.error = null;
    });
    builder.addCase(
      Actions.getRoteirosAlertas.fulfilled,
      roteirosAlertasReducer
    );
    builder.addCase(Actions.getRoteirosAlertas.rejected, (state, action) => {
      state.roteirosAlertas.isLoading = false;
      state.roteirosAlertas.error =
        action.error.message || "Erro ao carregar roteiros";
    });

    // Roteiros Criticidade
    builder.addCase(Actions.getRoteirosCriticidade.pending, (state) => {
      state.roteirosCriticidade.isLoading = true;
      state.roteirosCriticidade.error = null;
    });
    builder.addCase(
      Actions.getRoteirosCriticidade.fulfilled,
      roteirosCriticidadeReducer
    );
    builder.addCase(
      Actions.getRoteirosCriticidade.rejected,
      (state, action) => {
        state.roteirosCriticidade.isLoading = false;
        state.roteirosCriticidade.error =
          action.error.message || "Erro ao carregar criticidades dos roteiros";
      }
    );
  },
});

export const { changeTab, setActiveSubtabReducer, setFiltersForm } =
  torreLmlSlice.actions;

export default torreLmlSlice.reducer;
